import React from "preact";
import { useEffect, useState } from "preact/hooks";
import "../../scss/favorite-button.scss";
import { BoldOutlined, ItalicOutlined, MoreOutlined, UnderlineOutlined } from "@ant-design/icons";
import util from "../util";
import Quill, { Sources } from 'quill';
import Delta from "quill-delta";
import { unescapeHtml } from "../canvas/menu-object";

interface FormattableFieldProps {
  typeClass: string;
  typeId: string;
  curFieldText: string;
  contentEditMode?: string;
  tabIndex: number;
  updateItemValue: (key: string, value: string) => void;
  $element?: JQuery;
}

export default function FavoriteButton({ typeClass, typeId, curFieldText, contentEditMode, tabIndex, updateItemValue, $element }: FormattableFieldProps) {
  const [showSpecialChars, setShowSpecialChars] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [curEditMode, setCurEditMode] = useState('');

  useEffect(() => {
    if (curEditMode !== 'item' && contentEditMode === 'item') {
      util.nextTick(() => {
        initializeTextEditor();
      })
      setCurEditMode(contentEditMode);
    }
  }, [contentEditMode]);

  useEffect(() => {
    initializeTextEditor();
  }, []);

  function toggleToolbar(ev: MouseEvent) {
    setShowToolbar(!showToolbar);
  }

  function toggleSpecialChars(ev: MouseEvent) {
    setShowSpecialChars(!showSpecialChars);
  }

  function initializeTextEditor() {
    let currentText = curFieldText;
    if (!currentText && $element) {
      if (typeClass === 'group-name') {
        let foundName = $element!.find('.wrapper .mo-name')?.html();
        if (foundName) {
          foundName = unescapeHtml(foundName);
          currentText = foundName.trim();
        }
      } else if (typeClass === 'group-notes') {
        let foundNotes = $element!.find('.wrapper .mo-notes')?.html();
        if (foundNotes) {
          foundNotes = unescapeHtml(foundNotes);
          currentText = foundNotes.trim();
        }
      }
    }

    $(`#${typeId}Editor`).html(currentText);

    const editable = new Quill(`#${typeId}Editor`, {
      modules: {
        toolbar: `#${typeId}Toolbar`,
        keyboard: {
          bindings: {
            tab: { key: 9, handler: function () { return true; } },
            list: {
              key: 'backspace', context: { format: ['list'] },
              handler: function (range: any, context: any) {
                if (range.length === 0 && range.index !== 0) {
                  (this as any).quill.deleteText(range.index - 1, 1, Quill.sources.USER);
                } else {
                  (this as any).quill.deleteText(range, Quill.sources.USER);
                }
              }
            }
          }
        },
      },
      formats: ['bold', 'italic', 'underline'],
    });
    editable.on('text-change', (delta, oldDelta, sources) => onTextChange(delta, oldDelta, sources));
    $(`#${typeId}Editor`).find('.ql-editor').on('paste cut', (e) => {
      onPasteCut(e);
    })
    $(editable.getModule("toolbar").container).on('mousedown', (e) => {
      e.preventDefault();
    });
    const $qlEditor = $(`#${typeId}Editor .ql-editor`);
    $qlEditor.attr('tabindex', tabIndex);
    $qlEditor.on('focus', () => {
      $(`#${typeId}Editor`).addClass('focused');
    });
    $qlEditor.on('blur', () => {
      $(`#${typeId}Editor`).removeClass('focused');
      setShowSpecialChars(false);
      setShowToolbar(false);
    });
  }

  function onPasteCut(e: any) {
    updateTextOnCanvas($(`#${typeId}Editor`).find('.ql-editor')[0]);
  }

  function onTextChange(delta?: Delta, oldContents?: Delta, sources?: Sources) {
    updateTextOnCanvas($(`#${typeId}Editor`).find('.ql-editor')[0]);
  }

  function updateTextOnCanvas(qlElement: HTMLElement) {
    let toSetHTML = '';
    const blankValues = ['', '\n'];
    let innerText = qlElement.innerText;
    if (!blankValues.includes(innerText)) {
      toSetHTML = translateHTMLForCanvas(qlElement.innerHTML);
    }
    updateItemValue(typeClass, toSetHTML);
  }

  function translateHTMLForCanvas(html: any) {
    return html.replaceAll('</p><p', '<br></p><p').replaceAll('<br><br></p><p', '<br></p><p');
  }

  function addFieldCharacter(char: string) {
    const quillObj = Quill.find($(`#${typeId}Editor`)[0]);
    const range = quillObj.getSelection();
    if (range.length > 0) {
      quillObj.deleteText(range.index, range.length);
    }
    quillObj.insertText(range.index, char, {}, true);
    onTextChange();
  }

  const specialCharsList = (field: string, show: boolean) =>
    <div className={`spec-char-menu ${show ? 'show' : ''}`}>
      <button class="spec-char" onClick={() => addFieldCharacter('é')}>&#233;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('è')}>&#232;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('ê')}>&#234;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('á')}>&#225;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('ñ')}>&#241;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('ü')}>&#252;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('ç')}>&#231;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('à')}>&#224;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('ô')}>&#244;</button>
      <button class="spec-char" onClick={() => addFieldCharacter('œ')}>&#339;</button>
    </div>

  return <>
    <div className={`field ${typeClass}`} id={`${typeId}Editor`}></div>

    <div className={`toggle-${typeClass}-toolbar ${showToolbar ? 'active' : ''}`} onMouseDown={(ev) => ev.preventDefault()} onClick={(ev) => toggleToolbar(ev)}>
      <MoreOutlined />
    </div>
    <div id={`${typeId}Toolbar`} className={showToolbar ? 'show' : ''}>
      <button class="ql-bold"><BoldOutlined /></button>
      <button class="ql-italic"><ItalicOutlined /></button>
      <button class="ql-underline"><UnderlineOutlined /></button>
      <button class="add-special hint--bottom-left" data-hint="Special Characters" onClick={(ev) => toggleSpecialChars(ev)}>
        &#230;
      </button>
      {specialCharsList(typeId, showSpecialChars)}
    </div>
  </>
}
